import { useCallback, useEffect, useState, type JSX } from 'react';
import { ProductWorkAdministration } from './ProductWorkAdministration';
import { api } from '../api/client';
import type { TermDeckWorkbenchDto } from '../api/types';
import { workItemStateLabel } from '@term-deck/core/decision-copy.js';
import { useRouteHref } from '../lib/router';
import { maskMailSecrets } from '@term-deck/core/session-mail.js';

type Item = TermDeckWorkbenchDto['items'][number];

/**
 * AL.F5 (AUD.25) — a FILA do Produto na propria tela `/product`, com "encerrar item" para o que foi resolvido
 * FORA da fila (engenharia direta): o dono atesta o resultado e o item sai, sem abrir o workbench. Usa a
 * transicao `queued -> done` (resultado obrigatorio) e `-> rejected`, ambas humanas na fachada.
 */
export function ProductQueue(): JSX.Element | null {
  const href = useRouteHref();
  const sourceItemId = new URL(href, window.location.origin).searchParams.get('item');
  const [items, setItems] = useState<Item[] | null>(null);
  const [sourceItem, setSourceItem] = useState<Item | undefined>();
  const [snapshot, setSnapshot] = useState<TermDeckWorkbenchDto | null>(null);
  const [cursor, setCursor] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [closing, setClosing] = useState<string | null>(null);
  const [result, setResult] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const wb = await api.getTermDeckWorkbench({ view: 'resumo', state: 'open', limit: 20, ...(cursor ? { cursor } : {}) });
      if (!wb) throw new Error('queue_unavailable');
      setSnapshot(wb);
      setItems(wb?.items ?? []);
      if (sourceItemId) {
        const detail = await api.getTermDeckWorkbench({ view: 'completo', itemId: sourceItemId, limit: 1 });
        setSourceItem(detail?.items[0]);
      }
      setErro(null);
    } catch (e) {
      setErro(e instanceof Error && e.message.includes('cursor_changed') ? 'A fila mudou. Atualize o início para consultar a nova ordem.' : 'Não foi possível observar a fila. Tente atualizar.');
    } finally { setLoading(false); }
  }, [cursor, sourceItemId]);
  useEffect(() => {
    void load();
  }, [load]);

  const open = (items ?? []).filter((i) => i.state !== 'done' && i.state !== 'rejected');
  useEffect(() => { if (sourceItem) document.getElementById('product-source-item')?.scrollIntoView({ block: 'start' }); }, [sourceItem]);

  const advance = async (item: Item, state: 'done' | 'rejected'): Promise<void> => {
    if (state === 'done' && !result.trim()) {
      setMsg('diga o que foi feito — o resultado é obrigatório para encerrar.');
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      await api.advanceTermDeckWork(item.id, state, state === 'done' ? result.trim() : undefined);
      setClosing(null);
      setResult('');
      setMsg(state === 'done' ? 'item encerrado com o resultado registrado.' : 'item rejeitado.');
      await load();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'não foi possível avançar o item');
    } finally {
      setBusy(false);
    }
  };

  if (items === null && !erro) return null;
  return (
    <section className="product-queue" data-testid="product-queue" aria-label="Fila do produto">
      <h2>
        Fila do produto <span className="product-queue-count" data-testid="product-queue-count">{snapshot?.metrics?.open ?? (items ? open.length : '—')}</span>
      </h2>
      <p className="product-queue-msg" role="status">{snapshot?.page ? `${snapshot.page.offset + (open.length ? 1 : 0)}–${snapshot.page.offset + open.length} de ${snapshot.metrics?.open ?? snapshot.page.matched} abertos · ordem por prioridade declarada` : 'Consultando fila…'}{snapshot?.page?.fieldsTruncated ? ' · textos em excerto' : ''}{snapshot?.priority?.fullCoverage === 'UNKNOWN' ? ' · fonte FULL pendente ou indisponível' : ''}</p>
      <div className="product-queue-actions">
        <button type="button" className="product-queue-btn" disabled={loading} onClick={() => { if (cursor) setCursor(undefined); else void load(); }}>atualizar início</button>
        <button type="button" className="product-queue-btn" disabled={loading || !snapshot?.page?.nextCursor} onClick={() => setCursor(snapshot?.page?.nextCursor ?? undefined)}>próxima página</button>
      </div>
      {erro ? <p className="product-queue-msg" role="alert">{erro}</p> : null}
      {sourceItemId ? <aside id="product-source-item" className="product-queue-msg" aria-label="Item consultado na Gestão">
        {sourceItem ? <><h3>{maskMailSecrets(sourceItem.summary)}</h3><p>{workItemStateLabel(sourceItem.state)}{sourceItem.createdAtMs ? ` · criado em ${new Date(sourceItem.createdAtMs).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })}` : ''}</p><p>{maskMailSecrets(sourceItem.resultSummary ?? 'Sem resultado registrado.')}</p></> : <p>O item consultado não está disponível nesta fotografia da fila.</p>}
      </aside> : null}
      {open.length === 0 && !erro ? (
        <p className="product-queue-empty" data-testid="product-queue-empty">nada na fila — apontamentos novos entram aqui até o terminal do produto os assumir.</p>
      ) : (
        <ul className="product-queue-list">
          {open.map((item) => (
            <li key={item.id} className={`product-queue-item product-queue-item--${item.state}`} data-testid={`product-queue-item-${item.id}`}>
              <div className="product-queue-head">
                <span className={`product-queue-state product-queue-state--${item.state}`} title={item.state}>
                  {workItemStateLabel(item.state)}
                </span>
                <span className="product-queue-origin">{item.provenance === 'human_request' ? 'apontamento seu' : 'observação do MASTER'}</span>
              </div>
              <p className="product-queue-summary">{item.summary}</p>
              <ProductWorkAdministration itemId={item.id} onSaved={load} />
              {closing === item.id ? (
                <div className="product-queue-close">
                  <label htmlFor={`pq-result-${item.id}`}>o que foi feito</label>
                  <textarea
                    id={`pq-result-${item.id}`}
                    rows={2}
                    value={result}
                    onChange={(e) => setResult(e.target.value)}
                    placeholder="resultado em uma ou duas frases (obrigatório)"
                    data-testid={`product-queue-result-${item.id}`}
                  />
                  <div className="product-queue-actions">
                    <button type="button" className="product-queue-btn product-queue-btn--primary" disabled={busy} onClick={() => void advance(item, 'done')} data-testid={`product-queue-confirm-${item.id}`}>
                      confirmar encerramento
                    </button>
                    <button type="button" className="product-queue-btn" disabled={busy} onClick={() => { setClosing(null); setResult(''); setMsg(null); }}>
                      cancelar
                    </button>
                  </div>
                </div>
              ) : (
                <div className="product-queue-actions">
                  <button type="button" className="product-queue-btn" disabled={busy} onClick={() => { setClosing(item.id); setMsg(null); }} data-testid={`product-queue-close-${item.id}`}>
                    encerrar item
                  </button>
                  <button type="button" className="product-queue-btn product-queue-btn--danger" disabled={busy} onClick={() => void advance(item, 'rejected')} data-testid={`product-queue-reject-${item.id}`}>
                    rejeitar
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {msg ? <p className="product-queue-msg" data-testid="product-queue-msg">{msg}</p> : null}
    </section>
  );
}
